import { LegalPageLayout, type LegalSection } from '../ui/LegalPageLayout';

const EFFECTIVE_DATE = '11 July 2026';

const INTRO =
  'These Terms of Service ("Terms") govern your access to and use of DocCarePlus, a clinic management platform for healthcare providers and their patients. Please read them carefully. By creating an account or using the service, you agree to be bound by these Terms.';

const SECTIONS: LegalSection[] = [
  {
    heading: 'Acceptance of Terms',
    paragraphs: [
      'By registering for, accessing, or using DocCarePlus, you confirm that you have read, understood, and agree to these Terms and to our Privacy Policy. If you are using the service on behalf of a clinic or organisation, you represent that you are authorised to accept these Terms on its behalf.',
    ],
  },
  {
    heading: 'Description of Service',
    paragraphs: [
      'DocCarePlus provides tools for clinics to manage patient records, appointments, queues, prescriptions, billing, and staff access. Features may change, expand, or be discontinued as we improve the platform.',
    ],
  },
  {
    heading: 'Accounts & Registration',
    paragraphs: [
      'You must provide accurate and complete information when registering and keep it up to date. You are responsible for maintaining the confidentiality of your credentials and for all activity that occurs under your account. Notify us immediately of any unauthorised use.',
    ],
  },
  {
    heading: 'Acceptable Use',
    paragraphs: ['You agree not to:'],
    bullets: [
      'Use the service for any unlawful purpose or in violation of any applicable regulation.',
      'Access, store, or share data you are not authorised to handle.',
      'Attempt to disrupt, reverse-engineer, or gain unauthorised access to the platform or its systems.',
      'Upload malicious code or misuse the service in a way that harms other users.',
    ],
  },
  {
    heading: 'Clinical Responsibility (Not Medical Advice)',
    paragraphs: [
      'DocCarePlus is a software tool and does not provide medical advice, diagnosis, or treatment. Clinical decisions remain the sole responsibility of the treating healthcare professionals. You are responsible for the accuracy of the data you enter and for complying with all professional, legal, and regulatory obligations that apply to your practice.',
    ],
  },
  {
    heading: 'Subscriptions & Payments',
    paragraphs: [
      'Paid plans are billed in advance on the interval you select. Fees are non-refundable except where required by law. We may change pricing with reasonable notice, and continued use after a change takes effect constitutes acceptance of the new pricing.',
    ],
  },
  {
    heading: 'Data Ownership & Privacy',
    paragraphs: [
      'Clinics retain ownership of the patient and operational data they enter. We process that data to provide the service in accordance with our Privacy Policy. You are responsible for obtaining any consents required to store and process patient information.',
    ],
  },
  {
    heading: 'Intellectual Property',
    paragraphs: [
      'DocCarePlus, including its software, design, and branding, is owned by us and protected by intellectual property laws. These Terms grant you a limited, non-exclusive, non-transferable right to use the service; they do not transfer any ownership to you.',
    ],
  },
  {
    heading: 'Service Availability',
    paragraphs: [
      'We strive to keep the service available and reliable but do not guarantee uninterrupted access. The service may be temporarily unavailable for maintenance, updates, or reasons beyond our control.',
    ],
  },
  {
    heading: 'Termination',
    paragraphs: [
      'You may stop using the service at any time. We may suspend or terminate access if you breach these Terms or use the service in a way that risks harm to others or to the platform. Upon termination, provisions that by their nature should survive will continue to apply.',
    ],
  },
  {
    heading: 'Limitation of Liability',
    paragraphs: [
      'To the maximum extent permitted by law, DocCarePlus and its providers will not be liable for indirect, incidental, or consequential damages, or for loss of data, revenue, or profits arising from your use of the service. The service is provided "as is" without warranties of any kind.',
    ],
  },
  {
    heading: 'Changes to These Terms',
    paragraphs: [
      'We may update these Terms from time to time. When we make material changes, we will update the "Last updated" date above and, where appropriate, notify you. Continued use after changes take effect means you accept the revised Terms.',
    ],
  },
  {
    heading: 'Governing Law',
    paragraphs: [
      'These Terms are governed by the laws of India, without regard to conflict-of-law principles. Disputes will be subject to the exclusive jurisdiction of the competent courts located in Bangalore, India.',
    ],
  },
  {
    heading: 'Contact Us',
    paragraphs: ['If you have questions about these Terms, contact us at hello@doccareplus.com.'],
  },
];

export function TermsOfServicePage() {
  return (
    <LegalPageLayout
      title="Terms of Service"
      effectiveDate={EFFECTIVE_DATE}
      intro={INTRO}
      sections={SECTIONS}
    />
  );
}
